import type { Account } from '@/api/generated/model';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { NetWorthHistory } from '@/features/accounts/types';
import { useLanguage } from '@/i18n/language-hooks';
import { formatCurrency, useDefaultCurrency } from '@/lib/utils';
import { useMemo, useState } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

export type { NetWorthHistory } from '@/features/accounts/types';

interface NetWorthTrendCardProps {
  data: NetWorthHistory[];
  selectedAccount?: Account | null;
  isLoading?: boolean;
}

export const NetWorthTrendCard = ({ data, selectedAccount, isLoading }: NetWorthTrendCardProps) => {
  const { t } = useLanguage();
  const defaultCurrency = useDefaultCurrency();
  const [selectedPeriod, setSelectedPeriod] = useState('1y');

  // Generate static sinus wave loading data with upward trend
  const loadingData = useMemo(() => {
    const points = [];
    const baseValue = 45000; // Base value around $45k
    const now = new Date();

    for (let i = 0; i < 12; i++) {
      const date = new Date();
      date.setMonth(now.getMonth() - 11 + i);

      // Create sinus wave with upward trend
      const sinusValue = Math.sin((i / 12) * Math.PI * 2) * 5000; // ±$5k wave
      const trendValue = i * 1000; // $1k upward trend per month
      const value = baseValue + sinusValue + trendValue;

      points.push({
        date,
        value: Math.max(0, value),
        name: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      });
    }
    return points;
  }, []);

  // Get title and description based on selected account
  const getTitleAndDescription = () => {
    if (isLoading) {
      // Use the last value from loading data
      const lastBalance = loadingData[loadingData.length - 1].value;
      return {
        subtitle: selectedAccount ? selectedAccount.name : t('dashboard.netWorthTrend.title'),
        title: formatCurrency(lastBalance, defaultCurrency),
      };
    }

    // Get the last balance from the actual data
    const lastBalance = data && data.length > 0 ? data[data.length - 1].value : 0;

    if (selectedAccount) {
      return {
        subtitle: selectedAccount.name,
        title: formatCurrency(lastBalance, defaultCurrency),
      };
    } else {
      return {
        subtitle: t('dashboard.netWorthTrend.title'),
        title: formatCurrency(lastBalance, defaultCurrency),
      };
    }
  };

  const { subtitle, title } = getTitleAndDescription();

  // Filter data based on selected period and format labels
  const filteredData = useMemo(() => {
    if (!data || data.length === 0) return [];

    const now = new Date();
    let cutoffDate: Date;

    switch (selectedPeriod) {
      case '1m':
        cutoffDate = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
        break;
      case '3m':
        cutoffDate = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
        break;
      case '6m':
        cutoffDate = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
        break;
      case '1y':
        cutoffDate = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
        break;
      case 'ytd':
        cutoffDate = new Date(now.getFullYear(), 0, 1); // January 1st of current year
        break;
      case 'all':
      default:
        cutoffDate = new Date(now.getFullYear() - 5, now.getMonth(), now.getDate());
    }

    // Filter data based on actual date range
    const filteredData = data.filter((item) => {
      const itemDate = new Date(item.date);
      return itemDate >= cutoffDate;
    });

    // Sort data by date
    const sortedData = filteredData.sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    // Generate data points based on selected period
    const generateDataPoints = () => {
      const points: Array<{ date: Date; value: number; name: string }> = [];
      const currentYear = new Date().getFullYear();

      const currentDate = new Date(cutoffDate);
      const endDate = new Date(now);

      while (currentDate <= endDate) {
        let shouldAddPoint = false;

        switch (selectedPeriod) {
          case '1m':
            // Every day
            shouldAddPoint = true;
            break;
          case '3m':
            // Every Monday
            shouldAddPoint = currentDate.getDay() === 1;
            break;
          case '6m': {
            // Every 2 weeks on Monday
            const weekNumber = Math.floor(
              (currentDate.getTime() - cutoffDate.getTime()) / (7 * 24 * 60 * 60 * 1000)
            );
            shouldAddPoint = currentDate.getDay() === 1 && weekNumber % 2 === 0;
            break;
          }
          case '1y':
          case 'ytd':
          case 'all':
            // Every month on the 1st
            shouldAddPoint = currentDate.getDate() === 1;
            break;
        }

        if (shouldAddPoint) {
          // Find the closest data point before or on this date
          let value = 0;
          let lastKnownValue = 0;

          // Find the last known value before this date
          for (let i = sortedData.length - 1; i >= 0; i--) {
            const dataPoint = sortedData[i];
            const dataDate = new Date(dataPoint.date);
            if (dataDate <= currentDate) {
              lastKnownValue = dataPoint.value;
              break;
            }
          }

          // Use the last known value if we have data before this date, otherwise use 0
          value = lastKnownValue;

          // Format display name
          let displayName: string;
          const dateYear = currentDate.getFullYear();

          if (selectedPeriod === '1m' || selectedPeriod === '3m') {
            // For shorter periods, show the actual date
            displayName = currentDate.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: dateYear !== currentYear ? 'numeric' : undefined,
            });
          } else {
            // For longer periods (6m, 1y, ytd, all), show month and year if not current year
            if (dateYear !== currentYear) {
              displayName = currentDate.toLocaleDateString('en-US', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              });
            } else {
              displayName = currentDate.toLocaleDateString('en-US', {
                day: 'numeric',
                month: 'short',
              });
            }
          }

          points.push({
            date: new Date(currentDate),
            value,
            name: displayName,
          });
        }

        // Move to next day
        currentDate.setDate(currentDate.getDate() + 1);
      }

      return points;
    };

    return generateDataPoints();
  }, [data, selectedPeriod]);

  // Use loading data when loading, otherwise use filtered actual data
  const displayData = isLoading ? loadingData : filteredData;

  // Calculate performance based on display data
  const performance = useMemo(() => {
    if (!displayData || displayData.length < 2) return { value: 0, percentage: 0 };

    const startValue = displayData[0].value;
    const endValue = displayData[displayData.length - 1].value;
    const delta = endValue - startValue;
    const percentage = startValue !== 0 ? (delta / startValue) * 100 : 0;

    return { value: delta, percentage };
  }, [displayData]);

  // Get performance color
  const performanceColor = useMemo(() => {
    if (!displayData || displayData.length < 2) return 'text-muted-foreground';

    const startValue = displayData[0].value;
    const endValue = displayData[displayData.length - 1].value;
    const delta = endValue - startValue;

    return delta >= 0 ? 'text-fin-green' : 'text-fin-danger';
  }, [displayData]);

  // Get tooltip label based on selected account
  const getTooltipLabel = () => {
    return selectedAccount ? selectedAccount.name : 'Net Worth';
  };

  return (
    <Card className='shadow-xs'>
      <CardHeader>
        <div className='flex flex-col space-y-4 md:flex-row md:items-center md:justify-between md:space-y-0'>
          <div>
            <CardDescription className='text-muted-foreground text-sm'>{subtitle}</CardDescription>
            {isLoading ? (
              <Skeleton className='h-9 w-32' />
            ) : (
              <CardTitle className='text-3xl font-semibold'>{title}</CardTitle>
            )}
            {isLoading ? (
              <Skeleton className='mt-1 h-4 w-24' />
            ) : (
              <CardDescription className={`text-xs ${performanceColor}`}>
                {performance.value >= 0
                  ? `+${formatCurrency(performance.value, defaultCurrency)} (+${performance.percentage.toFixed(1)}%)`
                  : `-${formatCurrency(Math.abs(performance.value), defaultCurrency)} (${performance.percentage.toFixed(1)}%)`}
              </CardDescription>
            )}
          </div>
          <Tabs
            value={selectedPeriod}
            onValueChange={isLoading ? undefined : setSelectedPeriod}
            className='w-auto'
          >
            <TabsList className={`h-8 ${isLoading ? 'pointer-events-none opacity-50' : ''}`}>
              <TabsTrigger value='1m' className='px-2 text-xs'>
                {t('dashboard.period.1m')}
              </TabsTrigger>
              <TabsTrigger value='3m' className='px-2 text-xs'>
                {t('dashboard.period.3m')}
              </TabsTrigger>
              <TabsTrigger value='6m' className='px-2 text-xs'>
                {t('dashboard.period.6m')}
              </TabsTrigger>
              <TabsTrigger value='1y' className='px-2 text-xs'>
                {t('dashboard.period.1y')}
              </TabsTrigger>
              <TabsTrigger value='ytd' className='px-2 text-xs'>
                YTD
              </TabsTrigger>
              <TabsTrigger value='all' className='px-2 text-xs'>
                {t('dashboard.period.all')}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs
          value={selectedPeriod}
          onValueChange={isLoading ? undefined : setSelectedPeriod}
          className='w-full'
        >
          <TabsContent value={selectedPeriod} className='mt-0'>
            <div className='h-80'>
              <ResponsiveContainer width='100%' height='100%'>
                <LineChart data={displayData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray='3 3' horizontal={true} vertical={false} />
                  <XAxis
                    dataKey='name'
                    interval='preserveStartEnd'
                    tickFormatter={(value, index) => {
                      if (
                        index === 0 ||
                        index === Math.floor(displayData.length / 2) ||
                        index === displayData.length - 1
                      ) {
                        return value;
                      }
                      return '';
                    }}
                  />
                  <YAxis />
                  <Tooltip
                    formatter={(value) => [
                      formatCurrency(Number(value), defaultCurrency),
                      getTooltipLabel(),
                    ]}
                  />
                  <Line
                    type='monotone'
                    dataKey='value'
                    stroke={isLoading ? '#94a3b8' : '#1E5591'}
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                    strokeDasharray={isLoading ? '5,5' : undefined}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};
